import { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

/**
 * Bulk Set Invoice # dialog (Manus prototype follow-up to PR #219).
 * Every selected PO gets its own invoice number; only changed values are
 * persisted, each via the existing purchase-orders upsert endpoint.
 */
export const BulkInvoiceDialog = ({
    open,
    selectedPOs,
    onSubmit,
    onClose,
    testId
}) => {
    const [values, setValues] = useState({});

    useEffect(() => {
        if (open) {
            setValues(Object.fromEntries(selectedPOs.map((po) => [po.id, po.invoice || ''])));
        }
    }, [open]);

    if (!open) return null;

    const changedPOs = selectedPOs.filter((po) => (values[po.id] ?? '') !== (po.invoice || ''));

    const handleApplyClick = () => {
        if (changedPOs.length === 0) {
            toast.info('No invoice changes to apply.', { position: 'bottom-right' });
            return;
        }
        if (changedPOs.every((po) => !(values[po.id] || '').trim()) && changedPOs.length === selectedPOs.length) {
            toast.error('Enter at least one invoice number', { position: 'bottom-right' });
            return;
        }
        onSubmit(changedPOs.map((po) => ({ poId: po.id, invoice: (values[po.id] || '').trim() })));
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center overflow-y-auto p-4" data-testid={testId}>
            <div className="fixed inset-0 bg-gray-700 opacity-40" onClick={onClose} />
            <div className="relative w-full max-w-lg bg-white rounded-lg shadow-xl z-[61] max-h-[90vh] flex flex-col">
                <div className="flex items-start justify-between p-5 pb-2 shrink-0">
                    <div>
                        <h3 className="text-lg font-bold">Set Invoice # — {selectedPOs.length} PO{selectedPOs.length === 1 ? '' : 's'}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Match each PO to its supplier invoice. Every PO gets its own invoice number.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-3 space-y-2">
                    {selectedPOs.map((po) => (
                        <div key={po.id} className="flex items-center gap-3">
                            <div className="w-40 shrink-0 min-w-0">
                                <div className="font-semibold text-sm whitespace-nowrap">{po.poNumber}</div>
                                <div className="text-xs text-gray-500 truncate">{po.vendor}</div>
                            </div>
                            <input
                                type="text"
                                value={values[po.id] ?? ''}
                                placeholder="Invoice number"
                                onChange={(e) => setValues((prev) => ({ ...prev, [po.id]: e.target.value }))}
                                className="flex-1 text-sm border-gray-300 rounded-md"
                                aria-label={`Invoice number for ${po.poNumber}`}
                            />
                        </div>
                    ))}
                </div>
                <div className="flex items-center justify-between gap-2 p-5 pt-3 border-t border-gray-100 shrink-0">
                    <span className="text-sm text-gray-500">
                        {changedPOs.length} change{changedPOs.length === 1 ? '' : 's'} pending
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleApplyClick}
                            className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-gray-800 disabled:opacity-50"
                            disabled={changedPOs.length === 0}
                        >
                            Apply Invoice #s
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
